import {
  Button,
  Flex,
  FormField,
  InputPasswordField,
  InputTextField,
  Row,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useEffect, type KeyboardEvent } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { useSignIn } from '~/domain/Auth';
import { AuthErrorBanner, useAuthFormSubmit, useTurnstileToken } from '../shared';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const activateOnSpaceOrEnter = (handler: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    handler();
  }
};

export interface ILoginFormValues {
  email: string;
  password: string;
}

interface ILoginLocationState {
  prefillEmail?: string;
}

export const LoginForm = () => {
  const location = useLocation();
  const prefillEmail = (location.state as ILoginLocationState | null)?.prefillEmail ?? '';

  const methods = useForm<ILoginFormValues>({
    defaultValues: { email: prefillEmail, password: '' },
    mode: 'onSubmit',
  });

  return (
    <FormProvider {...methods}>
      <LoginFormInner />
    </FormProvider>
  );
};

const LoginFormInner = () => {
  const navigate = useNavigate();
  const { mutate: signIn, isPending, isError } = useSignIn();
  const {
    token: turnstileToken,
    widget: turnstileWidget,
    reset: resetTurnstile,
  } = useTurnstileToken();

  const { submit, bannerVisible, bannerRef } = useAuthFormSubmit<ILoginFormValues>({
    onValid: (values) => {
      signIn({ ...values, turnstileToken });
    },
  });

  useEffect(() => {
    if (isError) resetTurnstile();
  }, [isError, resetTurnstile]);

  const {
    register,
    formState: { errors },
  } = useFormContext<ILoginFormValues>();

  const goToForgotPassword = () => navigate('/forgot-password');
  const goToSignup = () => navigate('/signup');

  return (
    <form onSubmit={submit} data-testid="login-form" noValidate>
      <Typography bold color={colors.primary[500]} style={{ fontSize: 28, marginTop: 27 }}>
        Welcome Back, Offices!
      </Typography>
      <Typography
        size="body_large"
        color={colors.neutral[500]}
        style={{ display: 'block', marginTop: 18, lineHeight: 1.4 }}
      >
        Log in to connect with trusted dental professionals and keep your schedule covered.
      </Typography>

      {/* Wrapper keeps a stable scrollIntoView target when the banner is hidden. */}
      <div ref={bannerRef} style={{ marginTop: bannerVisible ? 20 : 0 }}>
        <AuthErrorBanner visible={bannerVisible} />
      </div>

      <div style={{ marginTop: bannerVisible ? 12 : 36 }}>
        <FormField label="Email">
          <InputTextField
            data-cy="input-email"
            placeholder="Enter your email"
            isError={Boolean(errors.email)}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: EMAIL_REGEX, message: 'Enter a valid email' },
            })}
          />
        </FormField>
      </div>

      <div style={{ marginTop: 32 }}>
        <FormField label="Password">
          <InputPasswordField
            data-cy="input-password"
            placeholder="Enter your password"
            isError={Boolean(errors.password)}
            {...register('password', { required: 'Password is required' })}
          />
        </FormField>
      </div>

      <Row justify="center" style={{ marginTop: 32, marginBottom: 16 }}>
        <Typography
          as="span"
          bold
          color={colors.secondary[500]}
          style={{ fontSize: 18, cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          data-cy="forgot-password-link"
          onClick={goToForgotPassword}
          onKeyDown={activateOnSpaceOrEnter(goToForgotPassword)}
        >
          Forgot Password?
        </Typography>
      </Row>

      {turnstileWidget}

      <Flex justify="center" style={{ marginTop: 28 }}>
        <Button
          data-cy="btn-log-in"
          type="submit"
          variant="primary"
          size="lg"
          isLoading={isPending}
          style={{ width: 296, borderRadius: 999 }}
        >
          Continue to GoTu
        </Button>
      </Flex>

      <Row justify="center" align="center" wrap="nowrap" gap={1} style={{ marginTop: 24 }}>
        <Typography size="body_large" color={colors.neutral[500]}>
          Don&apos;t have an account?
        </Typography>
        <Typography
          as="span"
          bold
          color={colors.secondary[500]}
          style={{ fontSize: 18, cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          data-cy="link-sign-up"
          onClick={goToSignup}
          onKeyDown={activateOnSpaceOrEnter(goToSignup)}
        >
          Register Now
        </Typography>
      </Row>
    </form>
  );
};
